import React, { memo } from "react"
import PropTypes from "prop-types"
import { Button } from "antd"
import { useTranslation } from "react-i18next"
import ErrorListItem from "./ErrorListItem"
import ImportErrorForm from "../ImportErrorForm"
import styles from "../ImportReport.module.css"

/**
 * Individual Fix Tab Component
 * Displays list of failed records with inline editing capability
 * Follows Single Responsibility Principle - handles individual fix UI
 * Memoized to prevent unnecessary re-renders
 *
 * @param {Object} props - Component props
 * @param {Object} props.failedRecords - Object containing failed records
 * @param {string|number} props.editingId - Currently editing row number
 * @param {Object} props.form - Ant Design form instance
 * @param {Function} props.onEditClick - Callback when edit button is clicked
 * @param {Function} props.onSave - Callback when save button is clicked
 * @param {Function} props.onCancel - Callback when cancel button is clicked
 * @param {boolean} props.isLoading - Loading state for save operation
 */
const IndividualFixTab = memo(({ failedRecords, editingId, form, onEditClick, onSave, onCancel, isLoading }) => {
  const { t } = useTranslation()

  return (
    <div className={styles.individualFixContent}>
      {Object.entries(failedRecords).map(([rowNumber, record]) => {
        const rowData = record.row || {}
        const messages = record.message || {}
        const fullName = [rowData?.first_name, rowData?.last_name].filter(Boolean).join(" ") || t("clients.import_report.no_name")
        const errorFields = Object.keys(messages)
        const isEditing = editingId === rowNumber

        // Render edit form if this row is being edited
        if (isEditing) {
          return (
            <div key={rowNumber} className={styles.editSection}>
              <div className={styles.rowInfoWrapper}>
                <div className={styles.rowInfoField}>
                  <span className={styles.rowNumber}>Row {rowNumber}</span>
                  <span className={styles.clientName}>{fullName}</span>
                </div>
                <Button
                  type="text"
                  shape="circle"
                  size="small"
                  className={styles.closeIcon}
                  onClick={onCancel}
                  aria-label="Close error message"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      onCancel()
                    }
                  }}
                  style={{ marginLeft: 0 }}
                >
                  &times;
                </Button>
              </div>

              <ImportErrorForm form={form} rowData={rowData} errorFields={errorFields} errorMessages={messages} />

              <div className={styles.formActions}>
                <Button
                  type="primary"
                  onClick={() => onSave(rowNumber, record)}
                  className={styles.saveButton}
                  disabled={isLoading}
                  loading={isLoading}
                >
                  {t("global.save")}
                </Button>
              </div>
            </div>
          )
        }

        // Render list item
        return (
          <ErrorListItem
            key={rowNumber}
            rowNumber={rowNumber}
            clientName={fullName}
            errorCount={Object.keys(messages).length}
            onEdit={() => onEditClick(rowNumber, rowData)}
          />
        )
      })}
    </div>
  )
})

IndividualFixTab.displayName = "IndividualFixTab"

IndividualFixTab.propTypes = {
  failedRecords: PropTypes.object.isRequired,
  editingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  form: PropTypes.object.isRequired,
  onEditClick: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
}

export default IndividualFixTab
